import React, { useState, useEffect } from 'react';
import {
  AppLayoutToolbar,
  BreadcrumbGroup,
  SideNavigation,
  PropertyFilterProps,
  SelectProps,
  Box,
  Spinner,
  Button,
} from '@cloudscape-design/components';
import { applyMode, Mode } from '@cloudscape-design/global-styles';
import DashboardPage from '../pages/DashboardPage';
import PipelinesPage from '../pages/PipelinesPage';
import PipelineDetailPage from '../pages/PipelineDetailPage';
import AIChatDrawer from './common/AIChatDrawer';
import { Pipeline, ChatMessage } from '../types';
import { apiService, ApiPipeline } from '../services/apiService';
import { useUser } from '../contexts/UserContext';

const AuthenticatedApp: React.FC = () => {
  const { user, givenName, session, isLoading, handleSignOut } = useUser();

  // State for current page
  const [currentPage, setCurrentPage] = useState<string>(() => {
    // Get initial page from URL hash or default to dashboard
    const hash = window.location.hash.slice(1); // Remove the #
    const [path] = hash.split('?'); // Remove query parameters for page detection
    if (path === '/pipelines') {
      return 'pipelines';
    } else if (path.startsWith('/pipelines/')) {
      return 'pipeline-detail';
    } else {
      return 'dashboard';
    }
  });

  // State for current pipeline name (for pipeline detail page)
  const [currentPipelineName, setCurrentPipelineName] = useState<string>(() => {
    const hash = window.location.hash.slice(1); // Remove the #
    const [path] = hash.split('?'); // Remove query parameters
    if (path.startsWith('/pipelines/')) {
      return path.slice(11); // Remove '/pipelines/'
    }
    return '';
  });

  // State for environment selector
  const [environment, setEnvironment] = useState<string>('gamma');

  // Handle environment changes with immediate state clearing
  const handleEnvironmentChange = React.useCallback(
    (newEnvironment: string) => {
      console.log(`Environment change requested: ${environment} -> ${newEnvironment}`);

      if (environment !== newEnvironment) {
        // Immediately clear ALL related state to prevent showing stale data
        setPipelines([]);
        setSelectedPipelines([]); // Clear selected pipelines
        setPipelinesLoading(true);
        _setPipelinesError(null); // Clear any errors

        // Clear any filtering to show all pipelines for new environment
        setFilteringQuery({
          operation: 'and',
          tokens: [],
        });

        // Don't clear cache - let each environment maintain its own cache
        // The apiService will handle environment-specific caching automatically

        console.log(`Cleared UI state for environment switch to: ${newEnvironment}`);

        // Update environment which will trigger loadPipelines via useEffect
        setEnvironment(newEnvironment);
      }
    },
    [environment],
  );

  // State for time period selector
  const [timePeriod, setTimePeriod] = useState<SelectProps.Option>({
    label: 'Weekly',
    value: 'weekly',
  });

  // State for dark mode toggle - detect system preference by default
  const [darkMode, setDarkMode] = useState<boolean>(() => {
    // Check localStorage first
    const savedTheme = localStorage.getItem('darkMode');
    if (savedTheme !== null) {
      return JSON.parse(savedTheme);
    }
    // Fall back to system preference
    return window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
  });

  // State for pipeline selection
  const [selectedPipelines, setSelectedPipelines] = useState<Pipeline[]>([]);

  // State for filtering
  const [filteringQuery, setFilteringQuery] = useState<PropertyFilterProps.Query>({
    operation: 'and',
    tokens: [],
  });

  // State for chat
  const [chatInput, setChatInput] = useState<string>('');
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const [chatLoading, setChatLoading] = useState<boolean>(false);
  const [conversationHistory, setConversationHistory] = useState<any[]>([]);

  // State for live pipelines data
  const [pipelines, setPipelines] = useState<Pipeline[]>([]);
  const [pipelinesLoading, setPipelinesLoading] = useState<boolean>(true);
  const [pipelinesError, _setPipelinesError] = useState<string | null>(null);

  // Filter pipelines based on the current filtering query
  const filteredPipelines = React.useMemo(() => {
    if (!filteringQuery.tokens || filteringQuery.tokens.length === 0) {
      return pipelines;
    }

    return pipelines.filter((pipeline) => {
      // Apply all filter tokens with AND logic
      return filteringQuery.tokens!.every((token) => {
        const { propertyKey, operator, value } = token;

        if (propertyKey === 'name') {
          const pipelineName = pipeline.name.toLowerCase();
          const filterValue = value.toLowerCase();

          switch (operator) {
            case '=':
              return pipelineName === filterValue;
            case '!=':
              return pipelineName !== filterValue;
            case ':':
              return pipelineName.includes(filterValue);
            case '!:':
              return !pipelineName.includes(filterValue);
            case '^':
              return pipelineName.startsWith(filterValue);
            case '!^':
              return !pipelineName.startsWith(filterValue);
            default:
              return pipelineName.includes(filterValue);
          }
        }

        if (propertyKey === 'tags') {
          if (!pipeline.tags) return false;
          const filterValue = value.toLowerCase();

          switch (operator) {
            case '=':
              return pipeline.tags.some((tag) => tag.toLowerCase() === filterValue);
            case '!=':
              return !pipeline.tags.some((tag) => tag.toLowerCase() === filterValue);
            default:
              return pipeline.tags.some((tag) => tag.toLowerCase().includes(filterValue));
          }
        }

        if (propertyKey === 'sns_source_name') {
          const arns: string[] = (pipeline as any).sns_sources || [];
          const topicNames = arns.map((arn: string) => (arn.split(':').pop() || '').toLowerCase());
          const filterValue = value.toLowerCase();

          switch (operator) {
            case '=':
              return topicNames.some((t) => t === filterValue);
            case '!=':
              return !topicNames.some((t) => t === filterValue);
            case ':':
              return topicNames.some((t) => t.includes(filterValue));
            case '!:':
              return !topicNames.some((t) => t.includes(filterValue));
            case '^':
              return topicNames.some((t) => t.startsWith(filterValue));
            case '!^':
              return !topicNames.some((t) => t.startsWith(filterValue));
            default:
              return topicNames.some((t) => t.includes(filterValue));
          }
        }

        if (propertyKey === 'destination_table_name') {
          const tables: string[] = (pipeline as any).destination_tables || [];
          const tableNames = tables.map((t: string) => t.toLowerCase());
          const filterValue = value.toLowerCase();

          switch (operator) {
            case '=':
              return tableNames.some((t) => t === filterValue);
            case '!=':
              return !tableNames.some((t) => t === filterValue);
            case ':':
              return tableNames.some((t) => t.includes(filterValue));
            case '!:':
              return !tableNames.some((t) => t.includes(filterValue));
            case '^':
              return tableNames.some((t) => t.startsWith(filterValue));
            case '!^':
              return !tableNames.some((t) => t.startsWith(filterValue));
            default:
              return tableNames.some((t) => t.includes(filterValue));
          }
        }

        return true;
      });
    });
  }, [pipelines, filteringQuery]);

  // Handle dark mode changes
  useEffect(() => {
    // Apply the visual mode using CloudScape's global styles
    applyMode(darkMode ? Mode.Dark : Mode.Light);

    // Save preference to localStorage
    localStorage.setItem('darkMode', JSON.stringify(darkMode));
  }, [darkMode]);

  // Listen for system theme changes
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (e: MediaQueryListEvent) => {
      // Only update if user hasn't manually set a preference
      const savedTheme = localStorage.getItem('darkMode');
      if (savedTheme === null) {
        setDarkMode(e.matches);
      }
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  // Parse URL parameters and set initial filter
  useEffect(() => {
    const parseUrlAndSetFilter = () => {
      const hash = window.location.hash.slice(1); // Remove the #
      const [path, queryString] = hash.split('?');

      if (path === '/pipelines' && queryString) {
        const params = new URLSearchParams(queryString);
        const tagParam = params.get('tag');

        if (tagParam) {
          setFilteringQuery({
            operation: 'and',
            tokens: [
              {
                propertyKey: 'tags',
                operator: '=',
                value: decodeURIComponent(tagParam),
              },
            ],
          });
        }
      }
    };

    // Parse initial URL on load
    parseUrlAndSetFilter();
  }, []);

  // Listen for hash changes to update routing
  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash.slice(1); // Remove the #
      const [path, queryString] = hash.split('?');

      if (path === '/pipelines') {
        setCurrentPage('pipelines');
        setCurrentPipelineName('');

        // Handle tag filtering from URL
        if (queryString) {
          const params = new URLSearchParams(queryString);
          const tagParam = params.get('tag');

          if (tagParam) {
            setFilteringQuery({
              operation: 'and',
              tokens: [
                {
                  propertyKey: 'tags',
                  operator: '=',
                  value: decodeURIComponent(tagParam),
                },
              ],
            });
          }
        } else {
          // Clear filters if no query parameters
          setFilteringQuery({
            operation: 'and',
            tokens: [],
          });
        }
      } else if (path.startsWith('/pipelines/')) {
        setCurrentPage('pipeline-detail');
        // Extract the pipeline identifier (could be filename or name)
        const pipelineIdentifier = path.slice(11); // Remove '/pipelines/'
        setCurrentPipelineName(pipelineIdentifier);
      } else if (path === '/dashboard' || path === '' || path === '/') {
        setCurrentPage('dashboard');
        setCurrentPipelineName('');
      } else {
        setCurrentPage(path); // Remove the '/'
        setCurrentPipelineName('');
      }
    };

    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  // Load pipelines data from API when user is authenticated or when environment changes
  const loadPipelines = React.useCallback(
    async (forceRefresh: boolean = false) => {
      if (!user || !session) {
        return;
      }

      try {
        setPipelinesLoading(true);
        _setPipelinesError(null);

        console.log(
          `Loading pipelines from API for environment: ${environment}...`,
          forceRefresh ? '(force refresh)' : '(cached if available)',
        );

        // Clear cache for the specific environment to prevent stale data
        if (forceRefresh) {
          apiService.clearEnvironmentCache();
        }

        const response = await apiService.listPipelines(session, forceRefresh, environment);

        console.log(`Raw API response contains ${response.pipelines.length} pipelines`);

        // Filter pipelines by environment first to avoid processing unnecessary data
        const environmentFilteredPipelines = response.pipelines.filter(
          (apiPipeline: ApiPipeline) => {
            if (apiPipeline.stage) {
              const stage = apiPipeline.stage.toLowerCase();
              const env = environment.toLowerCase();

              // When environment is "gamma", show both gamma and dev pipelines
              if (env === 'gamma') {
                return stage === 'gamma' || stage === 'dev';
              }

              // When environment is "prod", show only prod pipelines
              if (env === 'prod') {
                return stage === 'prod';
              }

              // For other environments, exact match
              return stage === env;
            }

            // If no stage property, include all pipelines (fallback)
            return true;
          },
        );

        console.log(
          `Environment filtering: ${environmentFilteredPipelines.length} pipelines match environment: ${environment}`,
        );

        // Remove duplicates by using both filename and name as unique identifiers
        // This handles cases where the same pipeline might appear multiple times
        const uniquePipelinesMap = new Map<string, ApiPipeline>();

        environmentFilteredPipelines.forEach((pipeline) => {
          // Create a unique key using both filename and name to handle edge cases
          const uniqueKey = `${pipeline.filename || 'no-filename'}-${pipeline.name || 'no-name'}`;

          // Only keep the first occurrence of each unique pipeline
          if (!uniquePipelinesMap.has(uniqueKey)) {
            uniquePipelinesMap.set(uniqueKey, pipeline);
          } else {
            console.log(`Removing duplicate pipeline: ${pipeline.name} (${pipeline.filename})`);
          }
        });

        const uniquePipelines = Array.from(uniquePipelinesMap.values());

        console.log(
          `Deduplication: Removed ${environmentFilteredPipelines.length - uniquePipelines.length} duplicate pipelines`,
        );

        // Transform API pipelines to match frontend Pipeline interface
        // Use filename as primary identifier, fallback to name if filename is missing
        const transformedPipelines: Pipeline[] = uniquePipelines.map(
          (apiPipeline: ApiPipeline, index: number) => ({
            id: `${environment}-${apiPipeline.filename || apiPipeline.name}-${index}`, // Environment-specific unique ID
            name: apiPipeline.name,
            filename: apiPipeline.filename, // Include filename for code repository links
            description: apiPipeline.description || '',
            status: normalizeStatus(apiPipeline.status),
            lastUpdated: apiPipeline.lastModified,
            recordsOut: apiPipeline.recordsOut,
            recordsIn: apiPipeline.recordsOut,
            tps: apiPipeline.tps,
            tags: apiPipeline.tags || [],
            statusIcon: getStatusIcon(apiPipeline.status),
            sns_sources: apiPipeline.sns_sources || [],
            destination_tables: apiPipeline.destination_tables || [],
            avgLatency: 0,
            minLatency: 0,
            maxLatency: 0,
          }),
        );

        // Only show active (non-disabled) pipelines
        const activePipelines = transformedPipelines.filter((p) => p.status !== 'disabled');
        setPipelines(activePipelines);
        console.log(
          `Successfully loaded ${activePipelines.length} active pipelines (filtered from ${transformedPipelines.length} total) for environment: ${environment}`,
        );
      } catch (error) {
        console.error('Error loading pipelines:', error);
        _setPipelinesError(error instanceof Error ? error.message : 'Failed to load pipelines');
        // Set empty array on error to show empty state
        setPipelines([]);
      } finally {
        setPipelinesLoading(false);
      }
    },
    [user, session, environment],
  ); // Add environment dependency to reload when environment changes

  // Initial load and reload when dependencies change
  useEffect(() => {
    loadPipelines();
  }, [loadPipelines]);

  // Handle manual refresh
  const handleRefreshPipelines = React.useCallback(() => {
    loadPipelines(true); // Force refresh
  }, [loadPipelines]);

  // Helper function to normalize status to expected Pipeline status values
  const normalizeStatus = (apiStatus: string): 'healthy' | 'error' | 'disabled' | 'in-alarm' => {
    switch (apiStatus.toLowerCase()) {
      case 'running':
      case 'active':
      case 'healthy':
      case 'ok':
        return 'healthy';
      case 'error':
      case 'failed':
      case 'failure':
        return 'error';
      case 'stopped':
      case 'inactive':
      case 'disabled':
        return 'disabled';
      case 'warning':
      case 'alarm':
      case 'degraded':
        return 'in-alarm';
      default:
        return 'healthy'; // Default to healthy for unknown statuses
    }
  };

  // Helper function to get status icon based on status
  const getStatusIcon = (status: string) => {
    const normalizedStatus = normalizeStatus(status);
    switch (normalizedStatus) {
      case 'healthy':
        return 'status-positive';
      case 'disabled':
        return 'status-stopped';
      case 'error':
        return 'status-negative';
      case 'in-alarm':
        return 'status-warning';
      default:
        return 'status-info';
    }
  };

  // Handle navigation
  const handleNavigation = (href: string) => {
    const page = href.slice(2); // Remove the #/
    if (page === 'pipelines') {
      setCurrentPage('pipelines');
      setCurrentPipelineName('');
    } else if (page.startsWith('pipelines/')) {
      setCurrentPage('pipeline-detail');
      setCurrentPipelineName(page.slice(10)); // Remove 'pipelines/'
    } else if (page === 'dashboard' || page === '') {
      setCurrentPage('dashboard');
      setCurrentPipelineName('');
    } else {
      setCurrentPage(page);
      setCurrentPipelineName('');
    }
    window.location.hash = href;
  };

  // Ref to track active typewriter interval so we can clean up
  const typewriterRef = React.useRef<ReturnType<typeof setInterval> | null>(null);

  // Cleanup typewriter on unmount
  useEffect(() => {
    return () => {
      if (typewriterRef.current) clearInterval(typewriterRef.current);
    };
  }, []);

  /**
   * Start a typewriter animation that reveals `fullText` character-by-character
   * on the message with `messageId`, updating chat state progressively.
   * Speed adapts: fast for long texts, slower for short ones.
   */
  const startTypewriter = React.useCallback((messageId: string, fullText: string) => {
    // Clear any previous typewriter
    if (typewriterRef.current) clearInterval(typewriterRef.current);

    let charIndex = 0;
    // Adaptive speed: ~15ms per char for long texts, ~30ms for short
    const charsPerTick = fullText.length > 500 ? 4 : fullText.length > 200 ? 2 : 1;
    const intervalMs = 15;

    typewriterRef.current = setInterval(() => {
      charIndex += charsPerTick;

      if (charIndex >= fullText.length) {
        // Animation complete — show full text, clear typing flag
        charIndex = fullText.length;
        if (typewriterRef.current) clearInterval(typewriterRef.current);
        typewriterRef.current = null;

        setChatMessages((prev) =>
          prev.map((msg) =>
            msg.id === messageId
              ? { ...msg, content: fullText, fullContent: undefined, isTyping: false }
              : msg,
          ),
        );
        setChatLoading(false);
        return;
      }

      // Reveal up to charIndex
      setChatMessages((prev) =>
        prev.map((msg) =>
          msg.id === messageId
            ? { ...msg, content: fullText.slice(0, charIndex), isTyping: true }
            : msg,
        ),
      );
    }, intervalMs);
  }, []);

  // Handle sending a chat message to the AI Agent backend
  const handleSendMessage = React.useCallback(async () => {
    const message = chatInput.trim();
    if (!message || chatLoading) return;

    // Add user message to chat
    const userMessage: ChatMessage = {
      id: `user-${Date.now()}`,
      role: 'user',
      content: message,
      timestamp: new Date(),
    };

    // Add loading placeholder for assistant response
    const loadingMessage: ChatMessage = {
      id: `assistant-${Date.now()}`,
      role: 'assistant',
      content: '',
      timestamp: new Date(),
      loading: true,
    };

    setChatMessages((prev) => [...prev, userMessage, loadingMessage]);
    setChatInput('');
    setChatLoading(true);

    try {
      const result = await apiService.sendChatMessage(message, session, conversationHistory);

      // Replace loading placeholder → start typewriter animation
      setChatMessages((prev) =>
        prev.map((msg) =>
          msg.id === loadingMessage.id
            ? {
                ...msg,
                content: '', // Start empty, typewriter fills it
                fullContent: result.response,
                loading: false,
                isTyping: true,
                error: result.error,
              }
            : msg,
        ),
      );

      // Update conversation history for context continuity
      if (result.conversation_history) {
        setConversationHistory(result.conversation_history);
      }

      // Kick off typewriter (chatLoading stays true until animation finishes)
      if (!result.error) {
        startTypewriter(loadingMessage.id, result.response);
      } else {
        // On error, show immediately
        setChatMessages((prev) =>
          prev.map((msg) =>
            msg.id === loadingMessage.id
              ? { ...msg, content: result.response, isTyping: false }
              : msg,
          ),
        );
        setChatLoading(false);
      }
    } catch (error) {
      // Replace loading message with error
      setChatMessages((prev) =>
        prev.map((msg) =>
          msg.id === loadingMessage.id
            ? {
                ...msg,
                content: 'Sorry, I encountered an error. Please try again.',
                loading: false,
                isTyping: false,
                error: true,
              }
            : msg,
        ),
      );
      setChatLoading(false);
    }
  }, [chatInput, chatLoading, session, conversationHistory, startTypewriter]);

  // Render page content based on current page
  const renderPageContent = () => {
    switch (currentPage) {
      case 'dashboard':
        return (
          <DashboardPage
            environment={environment}
            onEnvironmentChange={handleEnvironmentChange}
            timePeriod={timePeriod}
            onTimePeriodChange={setTimePeriod}
            darkMode={darkMode}
            onDarkModeChange={setDarkMode}
            givenName={givenName}
            pipelines={filteredPipelines}
            pipelinesLoading={pipelinesLoading}
            onRefresh={handleRefreshPipelines}
          />
        );
      case 'pipelines':
        return (
          <PipelinesPage
            environment={environment}
            onEnvironmentChange={handleEnvironmentChange}
            timePeriod={timePeriod}
            onTimePeriodChange={setTimePeriod}
            darkMode={darkMode}
            onDarkModeChange={setDarkMode}
            items={filteredPipelines}
            selectedItems={selectedPipelines}
            onSelectionChange={setSelectedPipelines}
            filteringQuery={filteringQuery}
            onFilteringChange={setFilteringQuery}
            loading={pipelinesLoading}
            onRefresh={handleRefreshPipelines}
          />
        );
      case 'pipeline-detail':
        return (
          <PipelineDetailPage
            pipelineName={currentPipelineName}
            environment={environment}
            onEnvironmentChange={handleEnvironmentChange}
            timePeriod={timePeriod}
            onTimePeriodChange={setTimePeriod}
            darkMode={darkMode}
            onDarkModeChange={setDarkMode}
          />
        );
      default:
        return (
          <DashboardPage
            environment={environment}
            onEnvironmentChange={handleEnvironmentChange}
            timePeriod={timePeriod}
            onTimePeriodChange={setTimePeriod}
            darkMode={darkMode}
            onDarkModeChange={setDarkMode}
            givenName={givenName}
            pipelines={filteredPipelines}
            pipelinesLoading={pipelinesLoading}
            onRefresh={handleRefreshPipelines}
          />
        );
    }
  };

  // Generate breadcrumbs based on current page
  const getBreadcrumbs = () => {
    switch (currentPage) {
      case 'dashboard':
        return [
          {
            href: '#/',
            text: 'Catalyst',
          },
          {
            href: '#/dashboard',
            text: 'Dashboard',
          },
        ];
      case 'pipelines':
        return [
          {
            href: '#/',
            text: 'Catalyst',
          },
          {
            href: '#/pipelines',
            text: 'Pipelines',
          },
        ];
      case 'pipeline-detail':
        // Find the pipeline to get its display name
        const pipeline = pipelines.find(
          (p) => p.filename === currentPipelineName || p.name === currentPipelineName,
        );
        const displayName = pipeline?.name || currentPipelineName;

        return [
          {
            href: '#/',
            text: 'Catalyst',
          },
          {
            href: '#/pipelines',
            text: 'Pipelines',
          },
          {
            href: `#/pipelines/${currentPipelineName}`,
            text: displayName,
          },
        ];
      default:
        return [
          {
            href: '#/',
            text: 'Catalyst',
          },
          {
            href: '#/dashboard',
            text: 'Dashboard',
          },
        ];
    }
  };

  // Show loading spinner while authentication is being checked
  if (isLoading) {
    return (
      <Box textAlign="center" padding="xxl">
        <Spinner size="large" />
        <Box variant="p" margin={{ top: 'm' }}>
          Checking authentication...
        </Box>
      </Box>
    );
  }

  // Show loading if no user yet (should redirect to login)
  if (!user || !givenName) {
    return (
      <Box textAlign="center" padding="xxl">
        <Spinner size="large" />
        <Box variant="p" margin={{ top: 'm' }}>
          Loading user information...
        </Box>
      </Box>
    );
  }

  return (
    <AppLayoutToolbar
      breadcrumbs={<BreadcrumbGroup items={getBreadcrumbs()} />}
      content={renderPageContent()}
      contentType={currentPage === 'dashboard' ? 'dashboard' : 'default'}
      tools={
        <Box padding="s">
          <Button variant="normal" onClick={handleSignOut}>
            Sign out
          </Button>
        </Box>
      }
      drawers={[
        {
          content: (
            <AIChatDrawer
              messages={chatMessages}
              chatInput={chatInput}
              onChatInputChange={setChatInput}
              onSendMessage={handleSendMessage}
              isLoading={chatLoading}
            />
          ),
          defaultSize: 350,
          id: 'aiChat',
          resizable: true,
          trigger: {
            iconName: 'gen-ai',
          },
          ariaLabels: {
            drawerName: 'AI Assistant',
            closeButton: 'Close AI Assistant',
            triggerButton: 'Open AI Assistant',
            resizeHandle: 'Resize AI Assistant panel',
          },
        },
      ]}
      navigation={
        <SideNavigation
          activeHref={`#/${currentPage}`}
          header={{
            href: '#/',
            text: 'Catalyst',
          }}
          onFollow={(event) => {
            if (event.detail.external) {
              // Let external links open naturally in new tab
              return;
            }
            event.preventDefault();
            handleNavigation(event.detail.href);
          }}
          items={[
            {
              href: '#/dashboard',
              text: 'Dashboard',
              type: 'link',
            },
            {
              href: '#/pipelines',
              text: 'Pipelines',
              type: 'link',
            },
            {
              defaultExpanded: true,
              items: [
                {
                  href: 'https://t.corp.amazon.com/create/options?category=ATS&type=ATS%20Europe&item=Catalyst&group=DTP%20Catalyst',
                  text: 'Cut Ticket',
                  type: 'link',
                  external: true,
                },
                {
                  href: 'https://w.amazon.com/bin/view/ATSEU/NMT/DnA/Data_Products/Catalyst',
                  text: 'Catalyst Wiki',
                  type: 'link',
                  external: true,
                },
              ],
              text: 'Support',
              type: 'section',
            },
            {
              items: [
                {
                  href: 'https://pipelines.amazon.dev/pipelines-wip/DTPCatalyst',
                  text: 'Pipeline',
                  type: 'link',
                  external: true,
                },
                {
                  href: 'https://code.amazon.com/packages/DTPCatalyst',
                  text: 'CDK Codebase',
                  type: 'link',
                  external: true,
                },
                {
                  href: 'https://t.corp.amazon.com/issues/?q=extensions.tt.status%3A%28Assigned%20OR%20Researching%20OR%20%22Work%20In%20Progress%22%20OR%20Pending%29%20AND%20extensions.tt.assignedGroup%3A%22DTP%20Catalyst%22',
                  text: 'SIMs Queue',
                  type: 'link',
                  external: true,
                },
              ],
              text: 'Quicklinks',
              type: 'section',
            },
          ]}
        />
      }
    />
  );
};

export default AuthenticatedApp;
